import { getEnv } from "../../../utils/env"
import React, { createContext, useReducer, Dispatch, useEffect } from "react"
import firebase from "firebase/compat/app"
import "firebase/auth"
import "firebase/firestore"
import {
  installCustomTokenLinkHandler,
  refreshCustomToken
} from "../helpers/customToken"

import {
  headerReducer,
  HeaderActions,
  // StudentDistrictReducer,
  HeaderStateType,
  themeReducer,
  adminFilterReducer,
  AdminFilterActions,
  ThemeActions,
  ActionTypeEnums,
  AdminFilterStateType,
  SubtestResetControlsStateType,
  SubtestResetControlsActions,
  subtestResetControlsReducer,
  DistrictReducer,
  DistrictState,
  UserReducer,
  UserState,
  setFbUser,
  setUserRecord,
  setDistrictRecord
} from "./reducers"

import {
  getDistrictById,
  getStudentById,
  getEducatorById
} from "../../mp-database"
import { useAuthImpersonationManage } from "../Impersonation"

type InitialStateType = {
  header: HeaderStateType
  theme: { name?: string }
  adminFilter: AdminFilterStateType
  subtestResetControls: SubtestResetControlsStateType
  district: DistrictState
  user: UserState
}

const initialState: InitialStateType = {
  header: {
    sharedParams: {},
    hideContent: false
  },
  theme: { name: "Management" },
  adminFilter: {
    districts: [],
    schools: [],
    teachers: [],
    classes: [],
    students: [],

    district: undefined,
    school: undefined,
    teacher: undefined,
    class: undefined,
    s: undefined,

    isFetchingDistricts: true,
    isFetchingSchools: false,
    isFetchingTeachers: false,
    isFetchingClasses: false,
    isFetchingStudents: false,

    isPaginatingDistricts: false,
    isPaginatingSchools: false,
    isPaginatingTeachers: false,
    isPaginatingClasses: false,
    isPaginatingStudents: false,
    // districts

    // for pagination
    districtsCursor: null,
    schoolsCursor: null,
    teachersCursor: null,
    classesCursor: null,
    studentsCursor: null,

    error: ""
  },
  subtestResetControls: {
    isPerformingAction: false
  },
  district: {
    record: null
  },
  user: {
    fbUser: null,
    record: null
  }
}

export type AppContextType = {
  state: InitialStateType
  dispatch: Dispatch<
    | HeaderActions
    | ThemeActions
    | AdminFilterActions
    | SubtestResetControlsActions
  >
}

const AppContext = createContext<AppContextType>({
  state: initialState,
  dispatch: () => null
})

const mainReducer = (
  {
    header,
    theme,
    adminFilter,
    subtestResetControls,
    district,
    user
  }: InitialStateType,
  action: any
  // | HeaderActions
  // | ThemeActions
  // | AdminFilterActions
  // | SubtestResetControlsActions
) => ({
  header: headerReducer(header, action as HeaderActions),
  theme: themeReducer(theme, action as ThemeActions),
  adminFilter: adminFilterReducer(adminFilter, action as AdminFilterActions),
  subtestResetControls: subtestResetControlsReducer(
    subtestResetControls,
    action as SubtestResetControlsActions
  ),
  district: DistrictReducer(district, action),
  user: UserReducer(user, action)
})
interface Prop {
  isAuthed: boolean
  children?: React.ReactNode
}
const AppProvider: React.FC<React.PropsWithChildren<Prop>> = ({
  isAuthed,
  children
}) => {
  const [state, dispatch] = useReducer(mainReducer, initialState)

  useAuthImpersonationManage({ isAuthed })

  useEffect(() => {
    firebase.auth().onAuthStateChanged(user => {
      dispatch(setFbUser(user))
    })

    firebase.auth().onIdTokenChanged(user => {
      if (user) {
        user
          .getIdToken()
          .then(idToken => refreshCustomToken(idToken))
          .catch(error => {
            console.log(`Error getting custom token: ${error}`)
          })
      }
    })

    // Pentest finding #2: qti-pwa links rendered before the in-memory custom
    // token exists get a fresh token when clicked.
    return installCustomTokenLinkHandler()
  }, [])

  useEffect(() => {
    // TODO: getStudent and getEductor should be listeners
    const user = state?.user?.fbUser
    if (user) {
      getStudentById(user.uid).then(doc => {
        if (doc) {
          dispatch(setUserRecord(doc.data))
        } else {
          getEducatorById(user.uid).then(doc => {
            if (doc) {
              dispatch(setUserRecord(doc.data))
            }
          })
        }
      })
    } else {
      dispatch(setUserRecord(null))
    }
  }, [state?.user?.fbUser, dispatch])

  useEffect(() => {
    const district = state?.user?.record?.schoolDistrict
    if (district) {
      getDistrictById(district).then(doc => {
        if (doc) {
          dispatch(setDistrictRecord(doc.data))
        }
      })
    } else {
      dispatch(setUserRecord(null))
    }
  }, [state?.user?.record, dispatch])

  return (
    //@ts-ignore
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  )
}

export { AppProvider, AppContext, ActionTypeEnums }
